import { useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import noop from 'lodash/noop';

import { rem } from '@blueprint/ui';

import { List, ListItem, Text } from 'components/blueprint';
import { useSubscribe } from 'hooks/useSubscribe';

import { ChatbotRichReplyBox } from './ChatbotRichReplyBox';
import { Paragraph } from './Paragraph';
import { ActionLabelPill, ActionPillWrapper } from './ActionPillWrapper';
import { EVENT_CHATBOT_FAQ } from './chatbot-reply-components/ChatbotReplyFaq/ChatbotReplyFaq';

export const ChatbotReply = forwardRef(
  (
    {
      answer: { content, datetime, id },
      connectors,
      dtree: { nodeID, objectID },
      onFaqLinkClick,
      onClickFeedback,
      isFeedbackEnable,
    },
    ref,
  ) => {
    const answerRef = useRef(null);

    useImperativeHandle(ref, () => ({
      scrollIntoView: () => answerRef.current?.scrollIntoView(),
    }));

    const handleFaqClick = useCallback(
      (topic, data) => {
        onFaqLinkClick(data);
      },
      [onFaqLinkClick],
    );

    useSubscribe(EVENT_CHATBOT_FAQ, handleFaqClick);

    const handleConnectorClick = ({ ConnectorID, DefaultClickText }) => {
      onClickFeedback({
        DTreeRequestType: 2,
        DTreeRequest: DefaultClickText,
        connector_ID: ConnectorID,
        ICS_SOURCE_ANSWER_ID: id,
        DTREE_OBJECT_ID: objectID,
        DTREE_NODE_ID: nodeID,
      });
    };

    return (
      <ChatbotRichReplyBox datetime={datetime}>
        <Paragraph as="div" ref={answerRef}>
          {content}
        </Paragraph>
        {connectors?.length > 0 && (
          <ActionPillWrapper>
            <List
              listStyleType="none"
              paddingLeft={0}
              margin={0}
              display="flex"
              flexWrap="wrap"
            >
              {connectors.map(
                ({ ConnectorID, DefaultClickText }) =>
                  DefaultClickText && (
                    <ListItem key={ConnectorID} marginRight={rem(8)}>
                      <ActionLabelPill
                        disabled={!isFeedbackEnable}
                        onClick={
                          isFeedbackEnable
                            ? (event) => {
                                event.preventDefault();
                                handleConnectorClick({
                                  ConnectorID,
                                  DefaultClickText,
                                });
                              }
                            : noop
                        }
                      >
                        <Text>{DefaultClickText}</Text>
                      </ActionLabelPill>
                    </ListItem>
                  ),
              )}
            </List>
          </ActionPillWrapper>
        )}
      </ChatbotRichReplyBox>
    );
  },
);
